import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { IconButton, Stack, TextField } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridSortModel,
} from "@mui/x-data-grid";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { gridSX } from "../../consts/gridSx";
import {
  FetchDataRequest,
  FetchDataResult,
  IFilterOption,
  IFilteringModel,
  IPaginationModel,
  ISortingModel,
} from "../../models/grid";
import styles from "./HeiGrid.module.scss";
import { usePersistentState } from "../../hooks/usePersistentState";

const areFiltersEqual = (left?: IFilteringModel, right?: IFilteringModel) =>
  JSON.stringify(left?.filters ?? []) === JSON.stringify(right?.filters ?? []);

interface HeiGridProps {
  columns: GridColDef[];
  fetchGridData: (request: FetchDataRequest) => Promise<FetchDataResult>;
  searchPlaceholder?: string;
  defaultSorting?: ISortingModel;
  handleRowClick?: GridEventListener<"rowClick">;
  refFunction?: any;
  filtering?: IFilteringModel;
  disableSearch?: boolean;
  storageSettingsKey: string;
  getRowId?: (row: any) => string;
  initialSearch?: string;
}
const ROW_HEIGHT = 52;
const HeiGrid = ({
  columns,
  fetchGridData,
  searchPlaceholder,
  defaultSorting,
  handleRowClick,
  refFunction,
  filtering,
  disableSearch,
  storageSettingsKey,
  getRowId,
  initialSearch,
}: HeiGridProps): JSX.Element => {
  const [localStorageSettings, setLocalStorageSettings] = usePersistentState(
    localStorage,
    storageSettingsKey,
    {
      pageSize: 10,
      sorting: defaultSorting,
    }
  );

  const [sessionStorageSettings, setSessionStorageSettings] =
    usePersistentState(sessionStorage, storageSettingsKey, {
      pageNumber: 1,
      search: "",
    });

  const [totalRows, setTotalRows] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [sortingModel, setSortingModel] = useState<ISortingModel | undefined>(
    localStorageSettings["sorting"]
  );
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    pageNumber: sessionStorageSettings["pageNumber"],
    pageSize: localStorageSettings["pageSize"],
  });
  const [filteringModel, setFilteringModel] = useState<IFilteringModel>(
    filtering ?? {
      filters: [],
    }
  );
  const [searchModel, setSearchModel] = useState<string>(
    initialSearch || sessionStorageSettings["search"]
  );
  const searchInpuRef = useRef<HTMLInputElement>();
  const hasInitialized = useRef(false);

  const debouncedHandleSearchKeyDown = useCallback(
    debounce(() => {
      submitSearch();
    }, 500),
    []
  );

  const submitSearch = () => {
    const searchValue = searchInpuRef.current!["value"];
    setSearchModel(searchValue);
    setFilteringModel({ filters: [] });
    setPaginationModel((pm) => ({ ...pm, pageNumber: 1 }));

    setSessionStorageSettings({
      pageNumber: paginationModel.pageNumber,
      search: searchValue,
    });
  };

  const fetchData = useCallback(async () => {
    setRows([]);
    setIsLoading(true);

    const { rows, totalRows } = await fetchGridData({
      pagination: paginationModel,
      sorting: sortingModel,
      search: searchModel,
      filtering: filteringModel,
    });
    setTotalRows(totalRows);
    setRows(rows);
    setIsLoading(false);
  }, [
    sortingModel,
    paginationModel,
    searchModel,
    fetchGridData,
    filteringModel,
  ]);

  useEffect(() => {
    fetchData();
    if (refFunction) refFunction.current = fetchData;
    if (searchInpuRef.current && !hasInitialized.current) {
      searchInpuRef.current.value = searchModel;
      hasInitialized.current = true;
    }
  }, [
    sortingModel,
    paginationModel,
    filteringModel,
    searchModel,
    fetchData,
    filteringModel,
  ]);

  const handleSortChange = (model: GridSortModel) => {
    const selectedSorting = model[0];
    let newSortingModel: ISortingModel | undefined = undefined;
    if (selectedSorting) {
      newSortingModel = {
        field: selectedSorting.field,
        sort: selectedSorting.sort,
      };
    }

    setSortingModel(newSortingModel);
    setLocalStorageSettings({
      pageSize: paginationModel.pageSize,
      sorting: newSortingModel,
    });
  };

  const handlePageChange = (page: number) => {
    setPaginationModel((pm) => ({ ...pm, pageNumber: page + 1 }));
    setSessionStorageSettings({ pageNumber: page + 1, search: searchModel });
  };

  const handleFilterChange = (activeFilters: IFilterOption[]) => {
    setFilteringModel({ filters: activeFilters });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationModel((pm) => ({ pageNumber: 1, pageSize: pageSize }));
    setLocalStorageSettings({ pageSize: pageSize, sorting: sortingModel });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value == "") {
      setSessionStorageSettings({
        pageNumber: 1,
        search: "",
      });
    }
  };

  useEffect(() => {
    if (filtering && !areFiltersEqual(filteringModel, filtering)) {
      handleFilterChange(filtering.filters);
    }
  }, [filtering]);

  return (
    <div>
      {!disableSearch && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mt: 2,
            mb: 3,
            ml: -1,
          }}
        >
          <IconButton
            type="submit"
            sx={{ p: "5px", marginTop: "14px" }}
            aria-label="search"
            onClick={submitSearch}
          >
            <SearchRoundedIcon />
          </IconButton>
          <TextField
            sx={{
              width: 450,
              mr: 1,
              "& .MuiFormLabel-root.MuiInputLabel-root": {
                color: "rgba(0, 0, 0, 0.38)",
              },
              "& .MuiInputBase-root.MuiInput-root.MuiInput-underline:before": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.38)",
              },
            }}
            type="search"
            variant="standard"
            label={searchPlaceholder || "Search"}
            onKeyDown={debouncedHandleSearchKeyDown}
            inputRef={searchInpuRef}
            onChange={handleSearchChange}
          />
        </Stack>
      )}
      <div
        data-testid="data-grid"
        className={styles.tableWrapper}
        style={{
          height: 111 + (rows.length > 0 ? rows.length * ROW_HEIGHT : 50),
        }}
      >
        <DataGrid
          sx={gridSX}
          rowHeight={ROW_HEIGHT}
          loading={isLoading}
          rows={rows}
          columns={columns}
          pageSize={paginationModel.pageSize}
          page={paginationModel.pageNumber - 1}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          onSortModelChange={handleSortChange}
          sortModel={sortingModel ? [sortingModel] : []}
          sortingMode="server"
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={handleRowClick}
          paginationMode="server"
          rowCount={totalRows}
          filterMode="server"
          getRowId={getRowId}
        />
      </div>
    </div>
  );
};

export default HeiGrid;
